import { LabelGroup } from "@dropbox/dash-component-library";
import { Toggle } from "@dropbox/dig-components/controls";
import { List } from "@dropbox/dig-components/list";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  BriefcaseLine,
  CelebrateLine,
  ClockLine,
  LocationLine,
  OutOfOfficeLine,
  SendLine,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfilePage } from "@mirage/analytics/events/types/click_dash_profile_page";
import { Link } from "@mirage/link/Link";
import {
  userInfoMapAtom,
  userProfileSettingsAtom,
} from "@mirage/mosaics/UserProfilePage/atoms";
import { formatDashGetPersonResponse } from "@mirage/mosaics/UserProfilePage/hooks";
import {
  calculateTenure,
  calculateTenurePercentile,
  formatBirthday,
  formatHireDate,
  formatLocation,
  formatUpcomingPTO,
  getLocalTime,
  isMoreThanToday,
  isValidEmail,
  toMetro,
} from "@mirage/mosaics/UserProfilePage/utils";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { callApiV2 } from "@mirage/service-dbx-api";
import { showSnackbar } from "@mirage/shared/snackbar";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import { useAtom, useSetAtom } from "jotai";
import { Fragment, FunctionComponent, useEffect, useState } from "react";

import type {
  UserInfo,
  UserProfileSettings,
} from "@mirage/mosaics/UserProfilePage/types";
import styles from "./UserProfileDetails.module.css";

export const UserProfileDetails = ({
  user,
  isEdit = false,
}: {
  user: UserInfo;
  isEdit?: boolean;
}) => {
  const ItemComponent = isEdit ? EditItem : Item;
  const currentAccount = useDropboxAccount();

  const calculateTimeDifference = (timezoneOffset: number) => {
    try {
      const userOffsetHours = timezoneOffset / 3600;
      const localOffsetHours = -new Date().getTimezoneOffset() / 60;
      const diff = userOffsetHours - localOffsetHours;

      const roundedDiff = Math.round(diff); // Round to nearest hour
      const absDiff = Math.abs(roundedDiff);

      if (absDiff === 0) {
        return i18n.t("same_timezone_as_yours");
      } else {
        return i18n.t("current_time_info", {
          countHours: absDiff,
          hours: absDiff === 1 ? i18n.t("hour") : i18n.t("hours"),
          aheadOrBehind:
            roundedDiff > 0
              ? i18n.t("timezone_ahead")
              : i18n.t("timezone_behind"),
        });
      }
    } catch (e) {
      return "";
    }
  };

  const isBoomerang = (tenure: number, hireDate: string): boolean => {
    const current = new Date();
    let [month, year] = hireDate.split("/").map(Number);
    if (!hireDate.includes("/")) {
      [year, month] = hireDate.split("-").map(Number);
    }

    const hired = new Date(year, month - 1, 1);

    let years = current.getFullYear() - hired.getFullYear();
    let months = current.getMonth() - hired.getMonth();
    if (months < 0) {
      years--;
      months += 12;
    }

    const monthsSinceHireDate = years * 12 + months;

    return Math.abs(tenure - monthsSinceHireDate) > 1;
  };

  const slackMessageLink = user.slack_message_link;
  const hasSlackLink = !!slackMessageLink?.length;
  const emailLink = isValidEmail(user.email) ? (
    <Link variant="monochromatic" hasNoUnderline href={`mailto:${user.email}`}>
      {user.email}
    </Link>
  ) : undefined;
  const details =
    hasSlackLink || emailLink
      ? [
          <ItemComponent
            key="contact"
            type="contact"
            icon={SendLine}
            primary={i18n.t("profile_contact")}
            secondary={
              hasSlackLink ? (
                <Link
                  variant="monochromatic"
                  hasNoUnderline
                  href={slackMessageLink}
                >
                  {"@" + user.display_handle}
                </Link>
              ) : (
                emailLink
              )
            }
            supporting={hasSlackLink ? emailLink : undefined}
            supportingSize="small"
          />,
        ]
      : [];

  const formattedCity = formatLocation(user.city);
  const formattedLocation = formattedCity || toMetro(user.location);
  if (formattedLocation || isEdit) {
    details.push(
      <ItemComponent
        key="location"
        type={isEdit ? "show_city" : "location"}
        icon={LocationLine}
        primary={isEdit ? i18n.t("detailed_location") : i18n.t("location")}
        secondary={isEdit ? formattedCity : formattedLocation}
        showToggle={isEdit}
      />,
    );
  }

  const timezoneOffset = user.timezone_offset;
  if (timezoneOffset) {
    details.push(
      <ItemComponent
        key="timezone"
        type="timezone"
        icon={ClockLine}
        primary={i18n.t("profile_current_time")}
        secondary={getLocalTime(timezoneOffset)}
        supporting={
          user.email !== currentAccount?.email
            ? calculateTimeDifference(timezoneOffset)
            : ""
        }
      />,
    );
  }

  if (user?.hire_date && user?.tenure != null) {
    details.push(
      <ItemComponent
        key="tenure"
        type="tenure"
        icon={BriefcaseLine}
        primary={i18n.t("tenure")}
        secondary={calculateTenure(user.tenure)}
        supporting={
          (isBoomerang(user.tenure, user.hire_date)
            ? i18n.t("first_started")
            : i18n.t("started")) +
          " " +
          formatHireDate(user.hire_date)
        }
        withTooltip={
          user.tenure_percentile
            ? i18n.t("tenure_percentile", {
                percentile: calculateTenurePercentile(user.tenure_percentile),
              })
            : undefined
        }
      />,
    );
  }

  const upcomingPTO = user.upcoming_pto_list?.[0];
  const hasUpcomingPTO =
    upcomingPTO?.start_date &&
    upcomingPTO?.end_date &&
    isMoreThanToday(upcomingPTO.start_date);
  if (hasUpcomingPTO || isEdit) {
    details.push(
      <ItemComponent
        key="pto"
        type={isEdit ? "show_pto" : "pto"}
        icon={OutOfOfficeLine}
        primary={i18n.t("profile_pto")}
        secondary={upcomingPTO ? formatUpcomingPTO(upcomingPTO) : undefined}
        showToggle={isEdit}
      />,
    );
  }

  const formattedBirthday = user.birthday
    ? formatBirthday(user.birthday)
    : undefined;
  if (formattedBirthday || isEdit) {
    details.push(
      <ItemComponent
        key="birthday"
        type={isEdit ? "show_birthday" : "birthday"}
        icon={CelebrateLine}
        primary={i18n.t("birthday")}
        secondary={formattedBirthday}
        showToggle={isEdit}
      />,
    );
  }

  return (
    <div className={styles.container} style={{ gap: isEdit ? "12px" : "0px" }}>
      {details.map((detail, index) => (
        <Fragment key={`user-profile-page-detail__${index}`}>{detail}</Fragment>
      ))}
    </div>
  );
};

const showToggleTypes = ["show_city", "show_pto", "show_birthday"];

interface ItemProps {
  type:
    | "contact"
    | "location"
    | "timezone"
    | "tenure"
    | "pto"
    | "birthday"
    | (typeof showToggleTypes)[number];
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
  primary: string;
  secondary?: string | React.ReactNode;
  supporting?: string | React.ReactNode;
  supportingSize?: "small" | "xsmall";
  withTooltip?: string;
  showToggle?: boolean;
}

const Item: FunctionComponent<ItemProps> = (props) => {
  return (
    <List.Item
      style={{
        display: "flex",
        alignItems: "flex-start",
        alignContent: "flex-start",
        width: "341px",
        padding: "12px 4px",
      }}
    >
      <BaseItemContent {...props} />
    </List.Item>
  );
};

const EditItem: FunctionComponent<ItemProps> = (props) => {
  const { showToggle, type } = props;
  const setUserInfoMap = useSetAtom(userInfoMapAtom);
  const [userProfileSettings, setUserProfileSettings] = useAtom(
    userProfileSettingsAtom,
  );
  const currentToggleSetting =
    !!userProfileSettings[type as keyof UserProfileSettings];
  const isToggleEnabled = showToggle && currentToggleSetting;
  const isToggleDisabled = showToggle && !currentToggleSetting;
  const { reportPapEvent } = useMirageAnalyticsContext();

  const handleToggle = async () => {
    if (showToggleTypes.includes(type)) {
      // Update toggle setting in UI
      const prevToggleSetting =
        userProfileSettings[type as keyof UserProfileSettings];
      const newUserProfileSettings = {
        ...userProfileSettings,
        [type]: !prevToggleSetting,
      };
      setUserProfileSettings(newUserProfileSettings);

      // Update user profile setting in backend
      const resp = await callApiV2("dashPeopleUpdateProfileSettings", {
        user_profile_settings: newUserProfileSettings,
      });
      const newUserInfo = formatDashGetPersonResponse(resp.person);
      if (newUserInfo) {
        setUserProfileSettings(newUserInfo.user_profile_settings);
        setUserInfoMap((prev) => ({
          ...prev,
          [newUserInfo.display_handle]: newUserInfo,
        }));

        reportPapEvent(
          PAP_Click_DashProfilePage({
            dashActionSurface: "dash_profile_page",
            dashProfileAction: "edit_save",
            featureLine: "people",
          }),
        );

        showSnackbar({ title: i18n.t("saved") });
      }
    }
  };

  return (
    <List.Item
      style={{
        display: "flex",
        alignItems: "flex-start",
        alignContent: "flex-start",
        height: "87px",
        width: "226px",
        padding: "12px 16px",
        border: isToggleDisabled
          ? "1px solid var(--dig-color__background__subtle)"
          : "1px solid var(--dig-color__border__subtle)",
        borderRadius: "12px",
        backgroundColor: isToggleDisabled
          ? "var(--dig-color__background__subtle)"
          : "var(--dig-color__background__base)",
      }}
    >
      <BaseItemContent {...props} isToggleEnabled={isToggleEnabled} />
      {showToggle && (
        <List.Accessory>
          <Toggle
            id={`edit-user-profile-page-toggle__${type}`}
            isToggled={!isToggleDisabled}
            onChange={handleToggle}
          />
        </List.Accessory>
      )}
    </List.Item>
  );
};

const BaseItemContent: FunctionComponent<
  ItemProps & { isToggleEnabled?: boolean }
> = ({
  icon,
  primary,
  secondary,
  supporting,
  withTooltip,
  supportingSize = "xsmall",
  isToggleEnabled = false,
}) => {
  const [secondaryText, setSecondaryText] = useState(secondary);

  useEffect(() => {
    // If private HR data is missing, we want to display "Not Available" text when toggle is enabled so
    // we need to wait for HR data to be fully loaded before showing secondary text
    const secondaryTextDelay = isToggleEnabled ? 500 : 0;
    const updateSecondaryTextTimeout = setTimeout(() => {
      const formattedSecondaryText =
        isToggleEnabled && !secondary ? i18n.t("not_available") : secondary;
      setSecondaryText(formattedSecondaryText);
    }, secondaryTextDelay);

    return () => clearTimeout(updateSecondaryTextTimeout);
  }, [secondary, isToggleEnabled]);

  return (
    <>
      <List.Accessory>
        <UIIcon
          color="var(--dig-color__text__base)"
          src={icon}
          isOpticallyAligned
        />
      </List.Accessory>
      <List.Content>
        <LabelGroup
          verticalAlignment="top"
          withLabel={<Text variant="label">{primary}</Text>}
          withSubText={
            <>
              {secondaryText && withTooltip && (
                <DigTooltip
                  title={withTooltip}
                  placement="bottom"
                  openDelay={0}
                >
                  <Text variant="label" size="small" color="subtle">
                    {secondaryText}
                  </Text>
                </DigTooltip>
              )}
              {secondaryText && !withTooltip && (
                <Text variant="label" size="small" color="subtle">
                  {secondaryText}
                </Text>
              )}
            </>
          }
        />
        <Text variant="label" size={supportingSize} color="subtle">
          {supporting}
        </Text>
      </List.Content>
    </>
  );
};
